/* eslint-disable no-lonely-if */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import cn from 'classnames';
import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { RegisterOptions, UseFormRegister } from 'react-hook-form';

import styles from './styles.module.scss';

interface InputProps extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  wrapperClassName?: string;
  inputClass?: string;
  focusClassName?: string;
  register?: UseFormRegister<any>;
  registerOptions?: RegisterOptions;
  errors?: { [key: string]: any };
  isUnderlined?: boolean;
  wrapperStyle?: React.CSSProperties;
  type?: string;
  enableCursorPos?: boolean;
}

const TextField = React.forwardRef(
  (
    {
      wrapperClassName,
      inputClass,
      focusClassName,
      wrapperStyle,
      name,
      register,
      registerOptions,
      errors,
      type,
      enableCursorPos,
      isUnderlined,
      onChange,
      onBlur,
      ...props
    }: InputProps,
    ref,
  ): ReactElement => {
    const inputRef = useRef<HTMLInputElement>(null);
    const cursorPositionRef = useRef<number | null>(null);
    useMemo(() => {
      if (cursorPositionRef.current !== null && inputRef.current) {
        inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
        cursorPositionRef.current = null;
      }
    }, [cursorPositionRef.current]);

    const removeExtraSpace = (s: string): string => s.trim().split(/ +/).join(' ');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const input = e.target;
      const oldValue = input.value;
      const oldCursor = input.selectionStart || 0;

      if (onChange) {
        onChange(e);
      }

      if (!enableCursorPos) return;

      // Use requestAnimationFrame or setTimeout to wait for DOM update
      requestAnimationFrame(() => {
        const newValue = input.value;
        const digitsBeforeCursor = oldValue.slice(0, oldCursor).replace(/\D/g, '').length;

        // Reconstruct expected cursor position based on digit count
        let digitCount = 0;
        let newCursorPos = 0;

        for (let i = 0; i < newValue.length; i++) {
          if (/\d/.test(newValue[i])) {
            digitCount++;
            if (digitCount > digitsBeforeCursor) break;
          }
          newCursorPos = i + 1;
        }

        // Special case: if user just typed the 2nd or 4th digit, move cursor after slash
        if (digitsBeforeCursor === 2 && newValue[2] === '/' && oldCursor === 2) {
          newCursorPos = 3;
        } else if (digitsBeforeCursor === 4 && newValue[5] === '/' && oldCursor === 5) {
          newCursorPos = 6;
        }

        input.setSelectionRange(newCursorPos, newCursorPos);
      });
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
      if (onBlur) onBlur(e);
      if (onChange) {
        const trimmedValue = removeExtraSpace(e.target.value);
        e.target.value = trimmedValue;
        onChange(e as React.ChangeEvent<HTMLInputElement>); // Manually trigger the onChange event
      }
    };

    const [errorMessage, setErrorMessage] = React.useState('');
    useEffect(() => {
      if (errors && Object.keys(errors).length > 0 && name) {
        const nameArray = name.split('.');
        if (nameArray.length > 1) {
          setErrorMessage(errors[nameArray[0]]?.[nameArray[1]]?.message);
        } else {
          setErrorMessage(errors[name]?.message);
        }
      } else {
        setErrorMessage('');
      }
    }, [errors, name, errors && Object.keys(errors)]);

    return (
      <div className={cn(styles.inputWrapper, wrapperClassName)} style={wrapperStyle} ref={ref as any}>
        <input
          className={cn(styles.input, inputClass, { 'is-invalid': errorMessage })}
          {...(register ? register(name!, registerOptions) : {})}
          type={type}
          {...props}
          disabled={props.disabled || false}
          maxLength={props.maxLength || 255}
          onChange={handleInputChange}
          onBlur={handleBlur}
          data-testid={`input-field-${name}`}
        />
        {isUnderlined && <span className={`${styles.inputFocus} ${focusClassName}`} />}
        {errorMessage && <div className={`${styles.invalidFeedback} invalid-feedback`}>{errorMessage}</div>}
      </div>
    );
  },
);

TextField.defaultProps = {
  wrapperClassName: '',
  type: 'text',
  inputClass: '',
  focusClassName: '',
  register: undefined,
  registerOptions: undefined,
  errors: undefined,
  isUnderlined: true,
  wrapperStyle: {},
  enableCursorPos: false,
};

export default TextField;
